import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/primitives";
import { MONOGRAM_URL } from "@/lib/assets";
import { useSiteContent } from "@/lib/useSiteContent";

const TOPICS = [
  ["Mindset", "/ideas/mindset"], ["Discipline", "/ideas/discipline"], ["Resilience", "/ideas/resilience"],
  ["Leadership", "/ideas/leadership"], ["Relationships", "/ideas/relationships"], ["Intentional Living", "/ideas/intentional-living"],
];
const NAV = [
  { label: "Home", to: "/" },
  { label: "Start Here", to: "/start-here" },
  { label: "Ideas", to: "/ideas", dropdown: true },
  { label: "Books", to: "/books" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [mIdeasOpen, setMIdeasOpen] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);
  const site = useSiteContent();
  const customLogo = site?.brand?.logo_url;

  useEffect(() => { setOpen(false); setIdeasOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openIdeas = () => { clearTimeout(closeTimer.current); setIdeasOpen(true); };
  const closeIdeas = () => { closeTimer.current = setTimeout(() => setIdeasOpen(false), 150); };

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 bg-[#FBFAF7]/90 backdrop-blur-md transition-all duration-300 ${scrolled ? "border-b border-[#E7E3DA] shadow-[0_1px_0_rgba(0,0,0,0.02)]" : "border-b border-transparent"}`}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16 sm:h-[72px]" : "h-[72px] sm:h-24"}`}>
          <Link to="/" data-testid="nav-logo" aria-label="Impactful Mindset 101 — home" className="flex items-center gap-2.5 sm:gap-3">
            {customLogo ? (
              <img src={customLogo} alt="Impactful Mindset 101" className={`w-auto object-contain transition-all duration-300 ${scrolled ? "h-9 sm:h-11" : "h-10 sm:h-12"}`} />
            ) : (
              <>
                <img src={MONOGRAM_URL} alt="Impactful Mindset 101 monogram" className={`w-auto object-contain transition-all duration-300 ${scrolled ? "h-9 sm:h-11" : "h-10 sm:h-12"}`} />
                <span className="flex flex-col leading-none">
                  <span className="font-serif-display font-bold text-[#0F2C59] tracking-tight text-lg sm:text-xl">IMPACTFUL</span>
                  <span className="text-[0.62rem] sm:text-xs tracking-[0.24em] text-[#414855] font-semibold mt-1">MINDSET <span className="text-[#B58A3E]">101</span></span>
                </span>
              </>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) =>
              n.dropdown ? (
                <div key={n.to} className="relative" onMouseEnter={openIdeas} onMouseLeave={closeIdeas}>
                  <Link
                    to={n.to}
                    data-testid="nav-ideas"
                    className={`inline-flex items-center gap-1 text-sm font-medium im-link-underline ${location.pathname.startsWith("/ideas") ? "text-[#0F2C59]" : "text-[#414855] hover:text-[#0F2C59]"}`}
                  >
                    {n.label} <ChevronDown size={14} className={`transition-transform ${ideasOpen ? "rotate-180" : ""}`} />
                  </Link>
                  {ideasOpen && (
                    <div data-testid="ideas-dropdown" className="absolute left-1/2 -translate-x-1/2 top-full pt-4">
                      <div className="w-60 bg-white border border-[#E7E3DA] shadow-xl py-2 im-reveal">
                        <Link to="/ideas" className="block px-5 py-2.5 text-sm font-semibold text-[#0F2C59] hover:bg-[#F3F0E9]">All Ideas</Link>
                        <div className="h-px bg-[#E7E3DA] mx-5 my-1" />
                        {TOPICS.map(([label, to]) => (
                          <Link key={to} to={to} className="block px-5 py-2.5 text-sm text-[#414855] hover:bg-[#F3F0E9] hover:text-[#0F2C59] transition-colors">{label}</Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-sm font-medium im-link-underline transition-colors ${location.pathname === n.to ? "text-[#0F2C59]" : "text-[#414855] hover:text-[#0F2C59]"}`}
                >
                  {n.label}
                </Link>
              )
            )}
          </nav>

          <div className="hidden lg:block">
            <Button to="/join" variant="primary" data-testid="nav-join-cta" className="text-xs">Join Impactful Mindset</Button>
          </div>

          <button data-testid="mobile-menu-toggle" aria-label="Toggle menu" aria-expanded={open} className="lg:hidden p-2 text-[#17181C]" onClick={() => setOpen(!open)}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#E7E3DA] bg-[#FBFAF7]">
          <nav className="px-5 py-4 flex flex-col">
            {NAV.map((n) =>
              n.dropdown ? (
                <div key={n.to} className="border-b border-[#E7E3DA]">
                  <button data-testid="mobile-nav-ideas" onClick={() => setMIdeasOpen(!mIdeasOpen)} className="w-full flex items-center justify-between py-3 text-base font-medium text-[#17181C]">
                    Ideas <ChevronDown size={18} className={`transition-transform ${mIdeasOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mIdeasOpen && (
                    <div className="pb-2">
                      <Link to="/ideas" className="block py-2 pl-4 text-sm font-semibold text-[#0F2C59]">All Ideas</Link>
                      {TOPICS.map(([label, to]) => (
                        <Link key={to} to={to} className="block py-2 pl-4 text-sm text-[#414855]">{label}</Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} className="py-3 text-base font-medium text-[#17181C] border-b border-[#E7E3DA]">
                  {n.label}
                </Link>
              )
            )}
            <Button to="/join" data-testid="mobile-nav-join-cta" className="mt-4 w-full">Join Impactful Mindset</Button>
          </nav>
        </div>
      )}
    </header>
  );
}
